import React from 'react'
import { Dialog as BaseDialog, styled } from '@mui/material'
import Swipeable from './Swipeable'
import { ImageProps, Overlay } from './Gallery'
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardIos'
import ControlsButton from './ControlsButton'
import { ArrowBackIcon } from './ArrowBackIcon'
import { CloseButton } from './CloseButton'

const IMAGE_DRAWER_HEIGHT: number = 104

interface FullScreenGalleryProps {
  images: ImageProps[]
  offset: number
  open: boolean
  onPrev: () => void
  onNext: () => void
  onClose: () => void
  setOffset: (offset: number | ((prev: number) => number)) => void
}

const MainImageArea = styled('div')({
  position: 'relative',
  flex: 1,
  overflow: 'hidden',
})

const MainImageWrapper = styled('div')({
  position: 'relative',
  width: '100%',
  height: '100%',
  '& > img': {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
})

const Dialog = styled(BaseDialog)({
  overflow: 'hidden',
  '& > * > *': {
    overflow: 'hidden',
  },
})

const DialogContent = styled('section')(({ theme }) => ({
  backgroundColor: 'black',
  color: 'white',
  display: 'flex',
  gap: 16,
  flexDirection: 'column',
  width: '100vw',
  height: '100vh',
  boxSizing: 'border-box',
  overflow: 'hidden',
  [theme.breakpoints.up('md')]: { padding: '0 80px' },
}))

const ImageDrawer = styled('div')(({ theme }) => ({
  height: IMAGE_DRAWER_HEIGHT,
  width: '100%',
  display: 'flex',
  gap: 8,
  justifyContent: 'center',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}))

const DrawerImageWrapper = styled('button')({
  height: 80,
  maxWidth: 120,
  position: 'relative',
  overflow: 'hidden',
  borderRadius: 8,
  flex: 1,
  color: 'none',
  display: 'block',
  textAlign: 'center',
  cursor: 'pointer',
  boxSizing: 'inherit',
  backgroundColor: 'none',
  margin: 0,
  padding: 0,
  border: 'none',
})

const DrawerImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
})

const DrawerOverlay = styled('div')({
  width: '100%',
  height: '80%',
  top: '50%',
  left: '50%',
  position: 'absolute',
  transform: 'translate(-50%, -50%)',
  '& .playButton': { width: '2rem', height: '2rem' },
  '& .MuiChip-root': { fontSize: '0.875rem' },
})

export const ControlsWrapper = styled('div')({
  position: 'fixed',
  display: 'flex',
  justifyContent: 'space-between',
  zIndex: 2,
  width: '100%',
  top: '50%',
  left: 0,
  transform: 'translate(0, -50%)',
  opacity: 0,
  transition: 'opacity 0.2s',
  pointerEvents: 'none',
  '*:hover &': {
    opacity: 1,
  },
  '@media (pointer: none)': {
    opacity: 1,
  },
})

const PhotoNumberWrapper = styled('div')(({ theme }) => ({
  display: 'none',
  paddingBottom: 10,
  textAlign: 'center',
  fontSize: '0.875rem',
  [theme.breakpoints.down('md')]: {
    display: 'block',
  },
}))

export const Controls = ({
  onPrev,
  onNext,
}: {
  onPrev: () => void
  onNext: () => void
}): JSX.Element => {
  return (
    <ControlsWrapper>
      <ControlsButton onClick={onPrev}>
        {' '}
        <ArrowBackIcon />
      </ControlsButton>
      <ControlsButton onClick={onNext}>
        <ArrowForwardIcon />
      </ControlsButton>
    </ControlsWrapper>
  )
}

const FullScreenGallery = ({
  offset,
  images,
  open,
  setOffset,
  onPrev,
  onNext,
  onClose,
}: FullScreenGalleryProps): JSX.Element => {
  return (
    <Dialog fullScreen fullWidth open={open} onClose={onClose}>
      <DialogContent>
        <MainImageArea>
          <Swipeable onLeftSwipe={onPrev} onRightSwipe={onNext}>
            {images.map((image, index) => {
              const isActive = index === Math.abs(offset % images.length)
              return (
                <MainImageWrapper
                  style={{ display: isActive ? 'block' : 'none' }}
                  key={image.src}
                >
                  {images.length > 1 && (
                    <Controls onNext={onNext} onPrev={onPrev} />
                  )}
                  <img
                    src={image.src}
                    alt=""
                    style={{ filter: image?.filter }}
                    onClick={image?.onClick}
                  />
                  {image?.overlay && <Overlay>{image?.overlay}</Overlay>}
                </MainImageWrapper>
              )
            })}
          </Swipeable>
        </MainImageArea>
        <ImageDrawer>
          {images.map((image, index) => {
            const isActive = index === Math.abs(offset % images.length)
            const inactiveFilter = 'brightness(50%)'
            const onClick = image.onClick ?? (() => setOffset(index))
            return (
              <DrawerImageWrapper key={image.src} onClick={onClick}>
                <DrawerImage
                  src={image.src}
                  alt=""
                  style={{ filter: !isActive ? inactiveFilter : '' }}
                />
                {image?.overlay && (
                  <DrawerOverlay>{image?.overlay}</DrawerOverlay>
                )}
              </DrawerImageWrapper>
            )
          })}
        </ImageDrawer>
        <PhotoNumberWrapper>
          {Math.abs(offset % images.length) + 1} / {images.length} photos
        </PhotoNumberWrapper>
        <CloseButton onClose={onClose} />
      </DialogContent>
    </Dialog>
  )
}

export default FullScreenGallery
